import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSettings } from '../../hooks/useMe';
import { AlertTriangle, Sparkles } from 'lucide-react';
import { useMailboxes } from '../../hooks/useMailboxes';
import { Card, CardBody, ErrorState, Notice, Skeleton, SkeletonLines, useToast } from '../../components/ui';
import { AccountsAddSection } from '../../components/settings/AccountsAddSection';
import { AccountsMailboxCard } from '../../components/settings/AccountsMailboxCard';
import { useOAuthConnect } from '../../components/settings/AccountsOAuth';
import { SettingsTitle } from './SettingsLayout';

export function AccountsSettings() {
  const q = useMailboxes();
  const oauth = useOAuthConnect();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [oauthError, setOauthError] = useState<string | null>(null);

  // Handle the OAuth redirect result once on mount.
  useEffect(() => {
    const connected = params.get('connected');
    const err = params.get('oauth_error');
    if (!connected && !err) return;
    if (connected) {
      toast.success(`${connected} connected`);
      void q.refetch();
    }
    if (err) setOauthError(err);
    const next = new URLSearchParams(params);
    next.delete('connected');
    next.delete('oauth_error');
    setParams(next, { replace: true });
  }, []);

  const onboarding = !useSettings()?.onboardedAt;
  const mailboxes = q.data?.mailboxes ?? [];
  const reauth = mailboxes.filter((m) => m.status === 'needs_reauth');

  return (
    <div>
      <SettingsTitle
        title="Email accounts"
        description="Connect several personal or business mailboxes. Credentials stay encrypted on the server, so signing in on another device restores everything."
      />
      <div className="space-y-6">
        {onboarding && (
          <Notice tone="info" icon={<Sparkles className="h-4 w-4" aria-hidden />}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>You're in the middle of setting up MailPilot.</span>
              <Link to="/welcome" className="font-semibold underline underline-offset-2">Continue setup →</Link>
            </div>
          </Notice>
        )}
        {oauthError && (
          <Notice tone="danger" icon={<AlertTriangle className="h-4 w-4" aria-hidden />}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-semibold">The provider sign-in did not complete</p>
                <p className="mt-0.5">{oauthError}</p>
              </div>
              <button type="button" className="font-semibold underline underline-offset-2" onClick={() => setOauthError(null)}>
                Dismiss
              </button>
            </div>
          </Notice>
        )}
        {reauth.length > 1 && (
          <Notice tone="warn" icon={<AlertTriangle className="h-4 w-4" aria-hidden />}>
            {reauth.length} mailboxes need reconnecting: {reauth.map((m) => m.email).join(', ')}.
          </Notice>
        )}

        {q.isLoading ? (
          <>
            {[0, 1].map((i) => (
              <Card key={i} aria-hidden>
                <CardBody className="space-y-5">
                  <div className="flex items-center gap-3.5">
                    <Skeleton className="h-12 w-12 rounded-[14px]" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-3 w-64 max-w-full" />
                    </div>
                    <Skeleton className="h-6 w-24 rounded-full" />
                  </div>
                  <div className="grid gap-6 sm:grid-cols-3">
                    <SkeletonLines lines={2} />
                    <SkeletonLines lines={2} />
                    <SkeletonLines lines={2} />
                  </div>
                </CardBody>
              </Card>
            ))}
          </>
        ) : q.isError ? (
          <Card>
            <ErrorState error={q.error} onRetry={() => void q.refetch()} title="Couldn't load your email accounts" />
          </Card>
        ) : (
          <>
            {mailboxes.map((mb) => (
              <AccountsMailboxCard key={mb.id} mb={mb} oauth={oauth} />
            ))}
            <AccountsAddSection providers={q.data?.providers ?? []} oauth={oauth} emptyState={mailboxes.length === 0} />
          </>
        )}
      </div>
    </div>
  );
}
